// Pacote Faturamento/PIX/Fichas, item 3 — compartilhar o QR Code (imagem)
// junto da mensagem no WhatsApp. Mecanismo tecnicamente correto: Web Share
// API (nível 2, arquivos) quando o navegador suporta — permite anexar o
// PNG do QR JÁ GERADO (nunca um QR diferente) junto do texto, com o
// usuário escolhendo WhatsApp na folha de compartilhamento do sistema.
//
// Ajuste complementar (item 2) — sem suporte a Web Share com arquivo, o
// comportamento anterior baixava o PNG automaticamente, o que na prática
// aparecia para o usuário como "o navegador abriu uma janela pra salvar
// arquivo" — uma UX confusa e não pedida. Agora o fallback NUNCA dispara
// download nenhum: só abre o WhatsApp com o texto (mecanismo de sempre).
// O QR continua disponível na tela normalmente (botão "Baixar QR Code"
// dedicado) para quem quiser anexar a imagem manualmente.
//
// NUNCA gera um QR novo aqui — recebe o canvas/base64 já renderizado pela
// própria tela (PixChaveModal/IntegratedPixModal), só converte para
// arquivo compartilhável quando a Web Share API pode usá-lo de verdade.

async function canvasToBlob(canvas) {
  return new Promise((resolve) => canvas.toBlob((blob) => resolve(blob), "image/png"));
}

async function base64PngToBlob(base64Png) {
  const res = await fetch(`data:image/png;base64,${base64Png}`);
  return res.blob();
}

// Correção visual final, item 2 — o QR compartilhado pelo Chrome chegava
// "enorme" no WhatsApp: o PIX por chave já é gerado localmente maior que
// o necessário pra compartilhar, e o PNG do PIX integrado vem do
// PROVEDOR (Mercado Pago/Asaas) em resolução de impressão, bem maior do
// que qualquer chat precisa. Uma versão ESPECÍFICA pra compartilhamento
// é gerada aqui — nunca altera o QR exibido dentro do LOOTUS (o modal
// continua usando o canvas/base64 originais pra tudo mais: download,
// copiar como imagem, exibição). Reescala a MESMA imagem já renderizada
// (nunca reconstrói o payload PIX) num canvas de destino com fundo
// branco — a quiet zone já faz parte da imagem original e fica
// proporcional ao reescalar uniformemente.
export const SHARE_QR_SIZE = 256;

async function resizeBlobForShare(sourceBlob, size = SHARE_QR_SIZE) {
  if (typeof createImageBitmap !== "function") return null; // sem suporte — compartilha a imagem original, nunca trava
  let bitmap;
  try {
    bitmap = await createImageBitmap(sourceBlob);
  } catch {
    return null;
  }
  try {
    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, size, size);
    ctx.drawImage(bitmap, 0, 0, bitmap.width, bitmap.height, 0, 0, size, size);
    if (typeof bitmap.close === "function") bitmap.close();
    return await new Promise((resolve) => canvas.toBlob((b) => resolve(b), "image/png"));
  } catch {
    return null;
  }
}

/**
 * @param {Object} params
 * @param {HTMLCanvasElement} [params.canvas] - canvas do QR já renderizado (PIX por chave)
 * @param {string} [params.base64Png] - QR já em base64 (PIX integrado, vindo do provedor)
 * @param {string} params.waLink - link wa.me já pronto (texto codificado)
 * @param {string} [params.filename]
 * @returns {Promise<{shared: boolean, cancelled?: boolean, fallback?: boolean, reason?: string}>}
 */
export async function shareQrToWhatsapp({ canvas, base64Png, waLink, filename = "pix-qrcode.png" }) {
  const textMatch = (waLink || "").split("?text=");
  const text = textMatch.length > 1 ? decodeURIComponent(textMatch[1]) : "";

  let blob = null;
  try {
    if (canvas) blob = await canvasToBlob(canvas);
    else if (base64Png) blob = await base64PngToBlob(base64Png);
  } catch {
    blob = null; // nunca derruba o compartilhamento por falha ao converter a imagem
  }

  if (blob) {
    // Item 2 — versão menor (~256x256) SÓ para o arquivo compartilhado;
    // se o redimensionamento não rolar por qualquer motivo, compartilha a
    // imagem original mesmo assim — nunca bloqueia o compartilhamento.
    const resized = await resizeBlobForShare(blob);
    if (resized) blob = resized;
    const file = new File([blob], filename, { type: "image/png" });
    const canShareFiles = typeof navigator.canShare === "function" && navigator.canShare({ files: [file] });
    if (canShareFiles && typeof navigator.share === "function") {
      try {
        await navigator.share({ files: [file], text });
        return { shared: true };
      } catch (e) {
        if (e?.name === "AbortError") return { shared: false, cancelled: true };
        // qualquer outra falha do Web Share cai no fallback abaixo — nunca trava o usuário
      }
    }
  }

  // Fallback — nunca finge que a imagem foi anexada e NUNCA dispara um
  // download automático (item 2 do ajuste complementar): abre o WhatsApp
  // só com o texto (mecanismo de sempre). Quando existe imagem mas o
  // ambiente não suporta anexá-la via Web Share, o chamador informa isso
  // claramente e oferece "Copiar QR Code"/"Baixar QR Code" como ações
  // explícitas — nunca um "salvar arquivo" disparado sozinho pelo clique
  // em "WhatsApp".
  window.open(waLink, "_blank", "noopener,noreferrer");
  return { shared: false, fallback: true, reason: blob ? "sem-suporte-anexo" : "sem-imagem" };
}

/** Correção — Modal Faturamento/Compartilhamento, item 2 (fallback): se o
 * navegador suportar a Clipboard API com imagens (ClipboardItem), permite
 * "Copiar QR Code" como imagem de verdade para a área de transferência —
 * nunca tenta colar automaticamente em lugar nenhum (o navegador não
 * permite). Mesmo QR já renderizado (canvas/base64), nunca gera um novo. */
export function canCopyQrImageToClipboard() {
  return typeof navigator !== "undefined" && !!navigator.clipboard
    && typeof navigator.clipboard.write === "function"
    && typeof window !== "undefined" && typeof window.ClipboardItem === "function";
}

/**
 * @param {Object} params
 * @param {HTMLCanvasElement} [params.canvas]
 * @param {string} [params.base64Png]
 * @returns {Promise<{copied: boolean, reason?: string}>}
 */
export async function copyQrImageToClipboard({ canvas, base64Png }) {
  if (!canCopyQrImageToClipboard()) return { copied: false, reason: "sem-suporte" };
  let blob = null;
  try {
    if (canvas) blob = await canvasToBlob(canvas);
    else if (base64Png) blob = await base64PngToBlob(base64Png);
  } catch {
    blob = null;
  }
  if (!blob) return { copied: false, reason: "sem-imagem" };
  try {
    await navigator.clipboard.write([new window.ClipboardItem({ [blob.type || "image/png"]: blob })]);
    return { copied: true };
  } catch {
    return { copied: false, reason: "falha" };
  }
}
